import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import {
  CustomDateErrorStateMatcher,
  CustomTelehealthErrorStateMatcher,
  greaterThanValidator,
} from '@app/shared/directives';
import { NotificationTypes, TableSubmissionStatus } from '@app/shared/enums';
import { dateFormatter, toLocalDate } from '@app/shared/utilities';
import {
  AppStoreState,
  GrantActions,
  GrantSelectors,
  NotificationActions,
} from '@app/store';
import {
  CustomReportingPeriod,
  CustomReportingPeriodPayload,
  GrantNumberOfSites,
  GrantSubmissionStatusDetails,
  ReportingPeriodState,
} from '@core/models';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { FormErrorsComponent } from '../form-errors/form-errors.component';

@Component({
  imports: [
    MatExpansionModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    FormErrorsComponent,
    MatDatepickerModule,
    MatDividerModule,
    MatButtonModule,
  ],
  selector: 'app-supplemental-info',
  templateUrl: './supplemental-info.component.html',
  styleUrls: ['./supplemental-info.component.scss'],
  standalone: true,
})
export class SupplementalInfoComponent implements OnDestroy, OnInit {
  expanded = input<boolean>(true);
  grantId = input.required<number>();
  isReviewPortal = input<boolean>(false);
  readOnly = input<boolean>(true);
  reportingPeriodState = input.required<ReportingPeriodState>();
  statusUpdated = output<TableSubmissionStatus>();

  readonly minDate = computed(() =>
    toLocalDate(this.reportingPeriodState()?.reportingPeriodStartDate),
  );
  readonly maxDate = computed(() =>
    toLocalDate(this.reportingPeriodState()?.reportingPeriodEndDate),
  );

  private actions$ = inject(Actions);
  private formBuilder = inject(UntypedFormBuilder);
  private store$ = inject(Store<AppStoreState.State>);

  public customReportingPeriod$ = this.store$.select(
    GrantSelectors.selectCustomReportingPeriod,
  );
  public dateMatcher = new CustomDateErrorStateMatcher();
  public telehealthMatcher = new CustomTelehealthErrorStateMatcher();
  public numberOfSites$ = this.store$.select(
    GrantSelectors.selectNumberOfSites,
  );
  public numberOfSitesStatusDetails: Record<
    TableSubmissionStatus,
    GrantSubmissionStatusDetails
  >;
  public numberErrorMessage = 'Must be a whole number';
  public numberOfSites: GrantNumberOfSites = {
    grantId: 0,
    titlexSubrecipients: '',
    titlexSites: '',
    differentFromApp: false,
    differentFromAppReason: '',
    telehealthClinics: '',
  };
  public supplementalInfoForm = new UntypedFormGroup({});
  public supplementalStatus: TableSubmissionStatus =
    TableSubmissionStatus.NotStarted;

  private destroyed$ = new Subject<boolean>();

  constructor() {
    this.actions$
      .pipe(
        ofType(
          GrantActions.deleteCustomReportingPeriodFailure,
          GrantActions.getCustomReportingPeriodFailure,
          GrantActions.updateCustomReportingPeriodFailure,
          GrantActions.getNumberOfSitesFailure,
          GrantActions.updateNumberOfSitesFailure,
        ),
        takeUntil(this.destroyed$),
        tap(({ message }) => {
          this.supplementalInfoForm.enable();
          this.store$.dispatch(
            NotificationActions.add({
              notificationType: NotificationTypes.DANGER,
              notificationText: message,
            }),
          );
        }),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(
          GrantActions.deleteCustomReportingPeriodSuccess,
          GrantActions.updateNumberOfSitesSuccess,
          GrantActions.updateCustomReportingPeriodSuccess,
        ),
        takeUntil(this.destroyed$),
        tap(() => {
          this.resetForm();
          this.fetchData();
          this.supplementalInfoForm.enable();
          this.store$.dispatch(
            NotificationActions.add({
              notificationType: NotificationTypes.SUCCESS,
              notificationText: 'Your information has been saved.',
            }),
          );
          setTimeout(() => {
            this.store$.dispatch(NotificationActions.reset());
          }, 3000);
        }),
      )
      .subscribe();

    this.numberOfSitesStatusDetails = {
      [TableSubmissionStatus.NotStarted]: {
        icon: 'block',
        class: 'notstarted',
      },
      [TableSubmissionStatus.InProgress]: {
        icon: 'pending',
        class: 'inprogress',
      },
      [TableSubmissionStatus.Completed]: {
        icon: 'check_circle_outline',
        class: 'complete',
      },
      [TableSubmissionStatus.NotRequired]: {
        icon: 'block',
        class: 'notrequired',
      },
    };
  }

  ngOnDestroy(): void {
    if (this.isReviewPortal()) {
      this.store$.dispatch(GrantActions.resetNumberOfSites());
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.buildForm();
    this.fetchData();

    combineLatest([this.numberOfSites$, this.customReportingPeriod$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([numberOfSites, customReportingPeriod]) => {
        const tableSubmissionStatus =
          numberOfSites?.status ?? TableSubmissionStatus.NotStarted;

        this.supplementalStatus = tableSubmissionStatus;
        this.statusUpdated.emit(tableSubmissionStatus);
        this.numberOfSites = numberOfSites;
        this.hydrateForm(numberOfSites, customReportingPeriod);
      });

    const differentFromAppControl =
      this.supplementalInfoForm.get('differentFromApp');
    if (differentFromAppControl) {
      differentFromAppControl.valueChanges.subscribe((value) =>
        this.setRequiredValidator(value),
      );
    }

    const customReportingPeriodsControl = this.supplementalInfoForm.get(
      'customReportingPeriods',
    );
    if (customReportingPeriodsControl) {
      customReportingPeriodsControl.valueChanges.subscribe((value) =>
        this.setDateRequiredValidator(value),
      );
    }
  }

  public disableSave(): boolean {
    return (
      this.supplementalInfoForm.invalid ||
      this.supplementalInfoForm.pristine ||
      this.supplementalInfoForm.disabled ||
      this.supplementalInfoForm.hasError(
        'customReportingPeriodFullYearSelected',
      )
    );
  }

  public buildForm(): void {
    this.supplementalInfoForm = this.formBuilder.group(
      {
        customReportingPeriods: [''],
        reportingPeriodStart: [''],
        reportingPeriodEnd: [''],
        titlexSubrecipients: [
          '',
          { validators: [Validators.pattern(/^[0-9]*$/)] },
        ],
        titlexSites: ['', { validators: [Validators.pattern(/^[0-9]*$/)] }],
        telehealthClinics: [
          '',
          { validators: [Validators.pattern(/^[0-9]*$/)] },
        ],
        differentFromApp: [''],
        differentFromAppReason: [''],
      },
      {
        validators: [
          greaterThanValidator(
            ['reportingPeriodStart', 'reportingPeriodEnd'],
            'invalidDateRange',
          ),
          greaterThanValidator(
            ['telehealthClinics', 'titlexSites'],
            'maxSitesExceeded',
          ),
        ],
      },
    );
    this.setCustomReportingPeriodFullYearSelectedValidator();

    if (this.readOnly()) {
      this.supplementalInfoForm.disable();
    }
  }

  public isDirty(): boolean {
    return !!this.supplementalInfoForm.dirty;
  }

  public resetForm(): void {
    if (!this.supplementalInfoForm.controls.customReportingPeriods.value) {
      this.supplementalInfoForm.patchValue({
        reportingPeriodStart: toLocalDate(
          this.reportingPeriodState().reportingPeriodStartDate,
        ),
        reportingPeriodEnd: toLocalDate(
          this.reportingPeriodState().reportingPeriodEndDate,
        ),
      });
    }

    if (!this.supplementalInfoForm.controls.differentFromApp.value) {
      this.supplementalInfoForm.patchValue({ differentFromAppReason: null });
    }

    this.supplementalInfoForm.markAsUntouched();
    this.supplementalInfoForm.markAsPristine();
  }

  public updateSupplementalInfoForm(): void {
    const grantId = this.grantId();

    const {
      differentFromApp,
      telehealthClinics,
      titlexSubrecipients,
      titlexSites,
      reportingPeriodStart,
      reportingPeriodEnd,
    } = this.supplementalInfoForm.value;

    const differentFromAppReason = this.supplementalInfoForm.controls
      .differentFromApp.value
      ? this.supplementalInfoForm.controls.differentFromAppReason.value
      : null;

    const payload: GrantNumberOfSites = {
      grantId,
      differentFromApp,
      differentFromAppReason,
      telehealthClinics,
      titlexSubrecipients,
      titlexSites,
    };

    const isCustomReportingPeriod =
      this.supplementalInfoForm.controls.customReportingPeriods.value;

    const reportingPayload: CustomReportingPeriodPayload =
      isCustomReportingPeriod
        ? {
            grantId,
            reportingPeriodStart: dateFormatter(reportingPeriodStart),
            reportingPeriodEnd: dateFormatter(reportingPeriodEnd),
          }
        : { grantId };

    const reportingPeriodAction = isCustomReportingPeriod
      ? GrantActions.updateCustomReportingPeriod
      : GrantActions.deleteCustomReportingPeriod;

    this.supplementalInfoForm.disable();

    this.store$.dispatch(GrantActions.updateNumberOfSites({ payload }));
    this.store$.dispatch(reportingPeriodAction({ reportingPayload }));
  }

  private fetchData(): void {
    this.store$.dispatch(
      GrantActions.getNumberOfSites({
        grantId: this.grantId(),
      }),
    );
    this.store$.dispatch(
      GrantActions.getCustomReportingPeriod({
        grantId: this.grantId(),
      }),
    );
  }

  private hydrateForm(
    data: GrantNumberOfSites,
    reportingPeriod: CustomReportingPeriod,
  ) {
    const reportingPeriodStart = reportingPeriod?.reportingPeriodStart
      ? toLocalDate(reportingPeriod.reportingPeriodStart.toString())
      : toLocalDate(this.reportingPeriodState().reportingPeriodStartDate);
    const reportingPeriodEnd = reportingPeriod?.reportingPeriodEnd
      ? toLocalDate(reportingPeriod.reportingPeriodEnd.toString())
      : toLocalDate(this.reportingPeriodState().reportingPeriodEndDate);

    const { ...formData } = data;
    const combinedData = {
      ...formData,
      ...reportingPeriod,
      customReportingPeriods: reportingPeriod !== null,
      reportingPeriodStart,
      reportingPeriodEnd,
    };

    this.supplementalInfoForm.patchValue({ ...combinedData });
    this.supplementalInfoForm.markAsPristine();
  }

  // -------------------------------------------------------------------------
  // Form validators
  // -------------------------------------------------------------------------

  private setCustomReportingPeriodFullYearSelectedValidator(): void {
    this.supplementalInfoForm.addValidators((): ValidationErrors | null => {
      const controls = this.supplementalInfoForm.controls;
      const reportingPeriodStartValue = controls.reportingPeriodStart.value;
      const reportingPeriodEndValue = controls.reportingPeriodEnd.value;

      const isCustomReportingPeriod = controls.customReportingPeriods.value;
      const isJanuaryFirst =
        new Date(reportingPeriodStartValue).getMonth() === 0 &&
        new Date(reportingPeriodStartValue).getDate() === 1;
      const isDecemberThirtyFirst =
        new Date(reportingPeriodEndValue).getMonth() === 11 &&
        new Date(reportingPeriodEndValue).getDate() === 31;
      const isFullYearSelected = isJanuaryFirst && isDecemberThirtyFirst;

      return isCustomReportingPeriod && isFullYearSelected
        ? { customReportingPeriodFullYearSelected: true }
        : null;
    });
  }

  private setDateRequiredValidator(isRequired: boolean) {
    if (isRequired) {
      this.supplementalInfoForm.controls.reportingPeriodStart.setValidators([
        Validators.required,
      ]);
      this.supplementalInfoForm.controls.reportingPeriodEnd.setValidators([
        Validators.required,
      ]);
    } else {
      this.supplementalInfoForm.controls.reportingPeriodStart.clearValidators();
      this.supplementalInfoForm.controls.reportingPeriodEnd.clearValidators();
    }
    this.supplementalInfoForm.controls.reportingPeriodStart.updateValueAndValidity();
    this.supplementalInfoForm.controls.reportingPeriodEnd.updateValueAndValidity();
  }

  private setRequiredValidator(isRequired: boolean) {
    if (isRequired) {
      this.supplementalInfoForm.controls.differentFromAppReason.setValidators([
        Validators.required,
      ]);
    } else {
      this.supplementalInfoForm.controls.differentFromAppReason.clearValidators();
    }
    this.supplementalInfoForm.controls.differentFromAppReason.updateValueAndValidity();
  }
}
